/**
 * Pure helpers for partitioning the outcome of a bulk action (e.g. the
 * Students tab "remind all debtors" flow) into disjoint succeeded/failed sets.
 *
 * The function is generic, deterministic, side-effect free and carries no
 * React/Apollo dependencies so it can be exercised in isolation.
 */

/** Outcome of processing a single item in a bulk action, aligned by index. */
export interface IBulkOutcome<TResult = unknown> {
  /** `true` when the per-item action succeeded. */
  ok: boolean;
  /** Result payload produced by a successful item, preserved on success. */
  result?: TResult;
  /** Error captured for a failed item, preserved on failure. */
  error?: unknown;
}

/** A successfully processed item together with its preserved result payload. */
export interface IBulkSuccess<TItem, TResult = unknown> {
  item: TItem;
  result?: TResult;
}

/** A failed item together with its captured error. */
export interface IBulkFailure<TItem> {
  item: TItem;
  error?: unknown;
}

/**
 * Disjoint partition of a bulk batch. The union of `succeeded` and `failed`
 * items equals the original batch with no loss and no duplication.
 */
export interface IBulkPartition<TItem, TResult = unknown> {
  succeeded: Array<IBulkSuccess<TItem, TResult>>;
  failed: Array<IBulkFailure<TItem>>;
}

/**
 * Partitions a batch of `items` into disjoint `succeeded` and `failed` sets
 * using the index-aligned `outcomes`.
 *
 * Guarantees:
 * - every item lands in exactly one partition (succeeded ∪ failed = items, and
 *   the two sets are disjoint);
 * - `failed` is exactly the items whose outcome was a failure, `succeeded`
 *   exactly those whose outcome was a success;
 * - successful results carry through any per-item `result` payload and failed
 *   results carry through any `error`;
 * - relative input order is preserved within each partition (determinism).
 *
 * An item with a missing outcome (when `outcomes` is shorter than `items`) is
 * treated as a failure so the partition remains total and lossless.
 *
 * @param items    The original batch of items that were acted upon.
 * @param outcomes Per-item outcomes aligned to `items` by index.
 */
export function partitionBulkResults<TItem, TResult = unknown>(
  items: ReadonlyArray<TItem>,
  outcomes: ReadonlyArray<IBulkOutcome<TResult>>,
): IBulkPartition<TItem, TResult> {
  const succeeded: Array<IBulkSuccess<TItem, TResult>> = [];
  const failed: Array<IBulkFailure<TItem>> = [];

  items.forEach((item, index) => {
    const outcome = outcomes[index];

    if (outcome && outcome.ok) {
      succeeded.push({ item, result: outcome.result });
    } else {
      failed.push({ item, error: outcome ? outcome.error : undefined });
    }
  });

  return { succeeded, failed };
}
